import type { MenuProps } from 'antd';
import { Badge, Dropdown, Form, Layout, Popover, Switch, notification } from 'antd';
import { t } from 'i18next';
import React, { useContext, useEffect, useState } from 'react';
import { AiOutlineMenu } from 'react-icons/ai';
import { useLocation, useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import styles from 'src/app/components/HeaderComponent/HeaderComponent.module.scss';
import showModal from 'src/app/components/ShowModalComponent/ShowModal';
import { formHelper, routeHelper, socketHelper } from 'src/app/helpers';
import Notifications from 'src/app/pages/NotificationsPage/Notifications';
import { useAppDispatch, useAppSelector } from 'src/app/redux/hook';

import alarmSound from 'public/alarm.mp3';
import notificationSound from 'public/msn.mp3';
import { InterestComponents, InterestsTypes } from 'src/enums/interests.enum';

import { BaseOptionType } from 'antd/es/select';
import FileUploadForm from 'src/app/forms/FileUploadForm/FileUploadForm';
import { getEnumKeyByValue } from 'src/app/helpers/enum.helper';
import { genericExcludeFilter, getLandingPage, getPermissionKey } from 'src/app/helpers/permission.helper';
import useInterestTableClick from 'src/app/hooks/useInterestTableClick';
import usePermissions from 'src/app/hooks/usePermissions';
import { headerPageSize } from 'src/app/pages/NotificationsPage/notification-list.config';
import { useLazyGetAllInterestsQuery } from 'src/app/redux/interests/interestsSlice';
import {
  addNewNotification,
  incrementUnreadCount,
  setHeaderNotifications,
  setNotificationsMuted,
  setUnreadInboxCount,
} from 'src/app/redux/notifications/notificationsSlice';
import { getNotifications, getUnreadNotificationsCount } from 'src/app/services/notifications/notfications.service';
import { routes } from 'src/configs/permissions.dictionary';
import { LAYOUTVIEW, LOGIN_URL } from 'src/configs/system.config';
import { RoutesEnum } from 'src/enums/route.enums';
import { LayoutViewType } from 'src/enums/user.enum';

import { MoonFilled, SunFilled } from '@ant-design/icons';
import { ThemeModeContext } from 'src/app/App';
import DebounceSelect from 'src/app/components/DebouncedSelectComponent/DebouncedSelect';
import { getLogoFromLayout } from 'src/app/components/HeaderComponent/header-component.config';
import Licenses from 'src/app/components/Licenses/Licenses';
import { useLazyGetRulesQuery } from 'src/app/redux/rules/rulesSlice';
import { logout, reset } from 'src/app/redux/user/userSlice';
import { NotificationResultType } from 'src/app/services/notifications/notifications-service.type';
import { getFile } from 'src/app/services/storage-server-service/storage.service';
import { NotificationArchiveStatusEnum } from 'src/enums/notifications.enum';
import { HeaderIcon, NotificationEmpty, NotificationMuted } from 'src/icons/index';
import { Header as HeaderTheme } from 'src/theme/Header.styled';

const LazyImportDataComponent = React.lazy(() => import('../ImportDataComponent/ImportDataComponent'));

const { Header } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

/* Function to get menu items in header dropdown 
It takes permissions parameter to check user permissions 
With the key parameter it gets the permission needed for that item 
*/
function getItem(
  menuKey: React.Key,
  permissions: string[],
  label: JSX.Element,
  key: React.Key,
  icon?: React.ReactNode,
  excludeLayouts?: LayoutViewType[],
  children?: MenuItem[],
): MenuItem {
  // start
  // Get the permission needed for item
  const routeKey = getEnumKeyByValue(RoutesEnum, key.toString());
  const checkedPermission = getPermissionKey([routes[routeKey]]);
  // end

  if (excludeLayouts && !genericExcludeFilter(excludeLayouts, LAYOUTVIEW())) return null;

  // If user does not have specified permission then remove that item from dropdown
  if (!permissions.includes(checkedPermission)) return null;
  return {
    key: menuKey,
    icon,
    children,
    label,
  } as MenuItem;
}

const overflowCount = LAYOUTVIEW() === LayoutViewType.SC3_LAYOUT ? 99 : Number.MAX_SAFE_INTEGER;
const HeaderComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const [notificationApi, contextHolder] = notification.useNotification({ stack: { threshold: 3 } });

  const { permissions } = usePermissions();
  const [isOpenSearch, setisOpenSearch] = useState<boolean>(false);
  const [fileUploadForm] = Form.useForm();
  const { id: userId, fullname, roles: userRoles, profilePicture } = useAppSelector((state) => state.user);

  const { headerNotifications, isMuted } = useAppSelector((state) => state.notificationsState);

  const { theme, logo } = useAppSelector((state) => state.configContent);
  const { notifications, selectedNotification, unreadInboxCount } = useAppSelector((state) => state.notificationsState);

  const [getRule] = useLazyGetRulesQuery();

  const [profilePictureSrc, setProfilePictureSrc] = useState<string>();

  /*
  Get profile image of user from storage server.
  This profile image is used to display in user icon next to the dropdown
  */
  useEffect(() => {
    const getProfileImage = async (filename: string | undefined) => {
      if (!filename) {
        setProfilePictureSrc('user2.png');
        return;
      }
      try {
        const { data } = await getFile({
          path: `profile-images/${userId}/${filename}`,
        });
        if (!data) return;
        let binary = '';
        const bytes = new Uint8Array(data);

        const len = bytes.byteLength;
        for (let i = 0; i < len; i += 1) {
          binary += String.fromCharCode(bytes[i]);
        }
        setProfilePictureSrc(`data:image/png;base64,${window.btoa(binary)}`);
      } catch (error) {
        console.log('error', error);
      }
    };

    getProfileImage(profilePicture);
  }, [profilePicture]);

  /* Get unread notifications count every time notifications is changed */
  useEffect(() => {
    const getUnreadAllNotificationsCount = async () => {
      try {
        const { data } = await getUnreadNotificationsCount();
        dispatch(setUnreadInboxCount(data));
      } catch (error) {
        console.log('error', error);
      }
    };

    if (LAYOUTVIEW() !== LayoutViewType.SAMA_BLACK_LAYOUT) {
      getUnreadAllNotificationsCount();
    }
  }, []);

  /*
    Getting notifications to display in header notification icon.
    It gets only few (with specified limit) notifications.
    It only fetches notifications that are in inbox (unarchived).
  */
  const getHeaderNotifications = async () => {
    try {
      const { data } = await getNotifications({
        limit: headerPageSize,
        offset: 0,
        archiveStatus: NotificationArchiveStatusEnum.UNARCHIVE,
      });
      dispatch(setHeaderNotifications(data.rows));
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (LAYOUTVIEW() !== LayoutViewType.SAMA_BLACK_LAYOUT) {
      getHeaderNotifications();
    }
  }, []);

  const { onInterestClick } = useInterestTableClick();

  const { mainTheme, setMainTheme } = useContext(ThemeModeContext);

  const [selectedInterest, setSelectedInterest] = useState<BaseOptionType>();
  const [notificationDropdownState, setNotificationDropdownState] = useState<boolean>(false);
  const [dropdownState, setDropdownState] = useState<boolean>(false);
  const [incomingNotification, setIncomingNotification] = useState<NotificationResultType>();
  const [getInterests] = useLazyGetAllInterestsQuery();
  /*
   When user logs out from dropdown menu item reset all states in redux.
   Then redirect user to the login page
  */
  const onLogout = async () => {
    await dispatch(logout());
    dispatch(reset());
    window.location.replace(LOGIN_URL);
  };

  useEffect(() => {
    socketHelper.createConnection();
  }, []);

  /* Create socket connection to listen notification changes */
  useEffect(() => {
    if (userId) {
      socketHelper.listenSocket(userId, (notificationData: NotificationResultType) => {
        setIncomingNotification(notificationData);
        dispatch(incrementUnreadCount());
        notificationApi.open({
          message: notificationData.subject,
          description: notificationData.content,
          duration: 5,
          placement: 'bottomLeft',
        });
      });
    }

    return () => {
      socketHelper.closeSocket();
    };
  }, [userId]);

  /*
  When a new notification is catched with socket check if that notification is muted.
  If it is not muted play the notification sound.
  Then get rule of that notification to check if the rule is also muted or not.
  If rule is not muted play alarm sound and show modal to stop alarm.
  */
  useEffect(() => {
    if (incomingNotification?.ruleId) {
      dispatch(addNewNotification(incomingNotification));
      if (!isMuted) new Audio(notificationSound).play();

      getRule({ ids: [incomingNotification.ruleId], blockSpinner: true })
        .unwrap()
        .then((data) => {
          const foundRule = data.rows[0];
          if (!foundRule?.sound) return;
          // Play alarm if rule is not muted
          const alarm = new Audio(alarmSound);
          alarm.loop = true;
          alarm.play();

          // Alarm modal
          showModal({
            title: 'ALARM',
            content: (
              <div>
                There is an alarm from <span style={{ fontWeight: '900' }}>{foundRule.subject}</span> rule
              </div>
            ),
            okText: 'Close',
            isClosable: false,
            disableCancelButton: true,
            onSave: (modal) => {
              alarm.pause();
              modal.destroy();
            },
          });
        })
        .catch((err) => console.log('err', err));

      setIncomingNotification(undefined);
    }
  }, [incomingNotification, isMuted]);

  // Dropdown items
  const items: MenuItem[] = [
    getItem(
      '1',
      permissions,
      <Link to="/Profile" onClick={() => setDropdownState(false)}>
        {t('header.menu.profile')}
      </Link>,
      RoutesEnum['/Profile'],
      undefined,
    ),
    getItem(
      '2',
      permissions,
      <Link to="/Settings" onClick={() => setDropdownState(false)}>
        {t('header.menu.settings')}
      </Link>,
      RoutesEnum['/Settings'],
    ),
    getItem(
      '3',
      permissions,
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <a
        type="text"
        onClick={() => {
          // eslint-disable-next-line no-sequences
          setDropdownState(false);
          const modal = showModal({
            title: t('header.menu.importData'),
            size: 'medium',
            content: (
              <LazyImportDataComponent
                isClickedModal={() => {
                  modal.destroy();
                }}
              />
            ),
            // onSave: () => form.validateFields().then((res) => form.submit()),
            // okText: t("global.export"),
            disableCancelButton: true,
            hasFooter: false,
          });
        }}
      >
        {t('header.menu.importData')}
      </a>,
      RoutesEnum['/ImportData'],
      undefined,
      [LayoutViewType.SAMA_RED_LAYOUT, LayoutViewType.NGINR_LAYOUT],
    ),
    getItem(
      '4',
      permissions,
      <Link to="/HelpAndSupport" onClick={() => setDropdownState(false)}>
        {t('header.menu.helpAndSupport')}
      </Link>,
      RoutesEnum['/HelpAndSupport'],
    ),

    getItem(
      '5',
      permissions,
      <Link to="/Administration" onClick={() => setDropdownState(false)}>
        {t('header.menu.administration')}
      </Link>,
      RoutesEnum['/Administration'],
    ),

    // getItem(
    //   permissions,
    //   // eslint-disable-next-line jsx-a11y/anchor-is-valid
    //   <a
    //     type="text"
    //     onClick={(event) => (
    //       // eslint-disable-next-line no-sequences
    //       setDropdownState(true),
    //       showModal({
    //         title: t("forms.exportDataForm.fields.title"),
    //         size: "large",
    //         content: <ExportDataForm form={form} />,
    //         onSave: (modal) =>
    //           form.validateFields().then((res) => {
    //             form.submit(), modal.destroy(), form.resetFields();
    //           }),
    //         okText: t("global.export"),
    //         disableCancelButton: true,
    //         event,
    //       })
    //     )}
    //   >
    //     {t("header.menu.exportData")}
    //   </a>,
    //   RoutesEnum["/ExportData"],
    //   undefined,
    //   [LayoutViewType.LAYOUT_2]
    // ),

    getItem(
      '6',
      permissions,
      <Link to="/Portal" onClick={() => setDropdownState(false)}>
        {/* {LAYOUTVIEW === LayoutViewType.LAYOUT_1 ? t('header.menu.SC3portal') : t('header.menu.SAMAportal')} */}
        {t('header.menu.portal')}
      </Link>,
      RoutesEnum['/Portal'],
    ),
    getItem(
      '7',
      permissions,
      <div
        className={styles.dropdownItem}
        onClick={() => {
          setDropdownState(false);
          showModal({
            title: t('header.menu.fileUpload'),
            size: 'xlarge-2',
            content: <FileUploadForm form={fileUploadForm} />,
            okText: t('global.upload'),
            onSave: (modal) => formHelper.submitForm(fileUploadForm, modal),
            disableCancelButton: true,
          });
        }}
      >
        {t('header.menu.fileUpload')}
      </div>,
      RoutesEnum['/FileUpload'],
      undefined,
      [LayoutViewType.SAMA_BLACK_LAYOUT],
    ),
    {
      label: (
        <div
          className={styles.dropdownItem}
          onClick={() => {
            setDropdownState(false);
            showModal({
              title: 'Third Party Libraries',
              content: <Licenses width={1170} height={700} />,
              size: 'xlarge-2',
              footer: [],
            });
          }}
        >
          {t('header.menu.licenses')}
        </div>
      ),
      key: '8',
    },
    {
      label: (
        <div className={styles.dropdownItem} onClick={onLogout}>
          {t('header.menu.logout')}
        </div>
      ),
      key: '9',
    },
  ];

  // Navigate to the notifications page if view all button is clicked
  const handleOnClickNotificationsViewAll = () => {
    setNotificationDropdownState(false);
    routeHelper.handleRoute(navigate, '/Notifications');
  };

  // Navigate to the notifications page if a notification is selected
  useEffect(() => {
    if (selectedNotification) {
      if (location.pathname !== '/Notifications') routeHelper.handleRoute(navigate, '/Notifications');
      setNotificationDropdownState(false);
    }
  }, [selectedNotification]);

  // When user change the url or select a interest from search bar in header clear selected interest
  useEffect(() => {
    setTimeout(() => {
      setSelectedInterest(undefined);
    }, 300);
  }, [location]);

  // Apply theme if it is stored in local storage
  useEffect(() => {
    try {
      const cachedTheme = localStorage.getItem('mainTheme');
      if (cachedTheme) {
        setMainTheme(cachedTheme);
        document.querySelector('body')?.setAttribute('theme', cachedTheme);
      } else {
        setMainTheme('light');
      }
    } catch (error) {
      console.log('err', error);
    }
  }, []);

  /* Theme setter function to change theme between light and dark modes 
   After changing it saves the new theme to the local storage
  */
  const setTheme = (isChecked: boolean) => {
    const newTheme = isChecked ? 'dark' : 'light';
    if (isChecked) {
      setMainTheme('dark');
    } else {
      setMainTheme('light');
    }
    localStorage.setItem('mainTheme', newTheme);
    document.querySelector('body')?.setAttribute('theme', newTheme);
  };

  // Notifications in header
  const notificationMenuComponent = (
    <div className={styles.notificationMenu}>
      <div className={styles.notificationMenuHeader}>
        <p>{t('header.notifications')}</p>
        <div className={styles.muteSwitch}>
          {/* Disabled or enable notification sound */}
          <Switch
            style={{ backgroundColor: '#F0F0F0', marginTop: '-5px' }}
            unCheckedChildren={
              <NotificationEmpty color="#92AABB" style={{ width: '15px', height: '15px', marginTop: '10px' }} />
            }
            checkedChildren={<NotificationMuted style={{ width: '15px', height: '15px', marginTop: '4px' }} />}
            checked={isMuted}
            onChange={(checked) => dispatch(setNotificationsMuted(checked))}
          />
        </div>
      </div>
      <div style={{ padding: 10, height: '45vh' }}>
        <Notifications
          dataSource={{
            data: headerNotifications,
          }}
          isStatic
        />
      </div>
      <div className={styles.buttonContainer}>
        <button className={styles.notificationMenuButton} type="button" onClick={handleOnClickNotificationsViewAll}>
          <p>{t('header.viewAll')}</p>
        </button>
      </div>
      <div className={styles.notificationMenuFooter} />
    </div>
  );

  /*
  Get logo function to show stored logo in redux and local storage
  If there is no logo stored then display default logo with getLogoFromLayout function
  */
  const getLogo = () => {
    if (LAYOUTVIEW() === LayoutViewType.SC3_LAYOUT)
      return (
        <a
          href="https://www.slavefreealliance.org"
          // eslint-disable-next-line react/jsx-no-target-blank
          target="_blank"
          rel="noreferrer"
          style={{ paddingLeft: '1.5rem' }}
        >
          <HeaderIcon />
        </a>
      );

    return (
      <div className={styles.logo} id="logo">
        <img
          onClick={() =>
            LAYOUTVIEW() === LayoutViewType.SAMA_BLACK_LAYOUT
              ? navigate('/Portal')
              : navigate(getLandingPage(userRoles))
          }
          src={logo || getLogoFromLayout(LAYOUTVIEW(), mainTheme)}
          alt=""
          loading="lazy"
          style={{ width: '8em', height: '6em', cursor: 'pointer' }}
        />
      </div>
    );
  };

  return (
    <HeaderTheme theme={theme}>
      {contextHolder}
      <Header className={styles.headerMain} dir="ltr">
        <div id="header" className={`${styles.header} headerTheme`}>
          {getLogo()}

          <div className={styles.userMenu} id="userMenu">
            {LAYOUTVIEW() !== LayoutViewType.SAMA_BLACK_LAYOUT ? (
              <div className={styles.searchButton} id="searchButton">
                {/* <SearchIcon color={theme.colors.quoteBgc} style={{ height: '28.95px', width: '28.95px' }} /> */}
                <img
                  alt="nSearch"
                  src={`/sidebar/Search_3D_${mainTheme === 'light' ? 'Black' : 'White'}.png`}
                  style={{ height: '31px', width: '31px', margin: 'auto' }}
                />
                <div className={styles.searchBorder} />
                {/* Global interest search bar */}
                <DebounceSelect
                  placeholder={t('header.search')}
                  variant="borderless"
                  showSearch
                  open={isOpenSearch}
                  onDropdownVisibleChange={(isOpen: boolean) => setisOpenSearch(isOpen)}
                  value={selectedInterest}
                  fetchOptions={getInterests}
                  suffixIcon={null}
                  style={{ width: '100%' }}
                  onSelect={(_, interest) => {
                    setSelectedInterest(interest);
                    if (
                      Number(interest.type) === InterestsTypes.Enterprise &&
                      LAYOUTVIEW() === LayoutViewType.SC3_LAYOUT
                    ) {
                      routeHelper.handleRoute(navigate, '/Network', {
                        id: interest.value as string,
                      });
                    } else {
                      onInterestClick(
                        [
                          {
                            id: interest.value as string,
                            name: interest.name,
                            type: interest.type,
                            classType: interest.classType,
                          },
                        ],
                        InterestComponents.NSearch,
                        [LayoutViewType.SAMA_RED_LAYOUT, LayoutViewType.NGINR_LAYOUT].includes(LAYOUTVIEW())
                          ? '/Interests'
                          : '/NSearch',
                      );
                    }
                  }}
                />
              </div>
            ) : null}

            <div className={styles.headerIcons}>
              {LAYOUTVIEW() !== LayoutViewType.SAMA_BLACK_LAYOUT ? (
                <div className={styles.notificationIcon} id="notificationIcon">
                  <Popover
                    overlayClassName="header-notifications-modal"
                    placement="bottom"
                    content={notificationMenuComponent}
                    trigger="click"
                    style={{ background: 'transparent' }}
                    destroyTooltipOnHide
                    open={notificationDropdownState}
                    onOpenChange={(visible: boolean) => {
                      setNotificationDropdownState(visible);
                    }}
                  >
                    <Badge
                      count={unreadInboxCount}
                      overflowCount={overflowCount}
                      size="default"
                      style={{
                        background: 'transparent',
                        color: theme.colors.quoteBgc,
                        border: 'none',
                        borderColor: 'transparent',
                        fontSize: 13,
                        fontWeight: 'bold',
                      }}
                      offset={[-14, 5]}
                      title={unreadInboxCount > overflowCount ? unreadInboxCount.toString() : undefined}
                    >
                      {/* <NotificationEmpty
                        style={{
                          width: '26.15px',
                          height: '26.15px',
                          marginTop: '5px',
                          cursor: 'pointer',
                        }}
                        color="#92aabb"
                      /> */}
                      <div>
                        <img
                          alt="notification"
                          src={`/sidebar/Notification_3D_${mainTheme === 'light' ? 'Black' : 'White'}.png`}
                          style={{ height: '31px', width: '31px', margin: 'auto', cursor: 'pointer' }}
                          onClick={getHeaderNotifications}
                        />
                      </div>
                    </Badge>
                  </Popover>
                </div>
              ) : null}
              <div className={styles.mainThemeSwitcherDiv} id="theme-switcher-container">
                <Switch
                  className={styles.mainThemeSwitcher}
                  id="theme-switcher"
                  style={{
                    background:
                      mainTheme === 'dark'
                        ? 'linear-gradient(to right, #979797, #fff)'
                        : 'linear-gradient(to right, #cfcfcf, #979797, #626262, #323232, #000000)',
                  }}
                  onChange={(checked: boolean) => {
                    setTheme(checked);
                  }}
                  value={mainTheme === 'dark'}
                  unCheckedChildren={
                    <MoonFilled style={{ color: 'white', fontSize: '16px', marginTop: '-2.5px', marginLeft: '-8px' }} />
                  }
                  checkedChildren={
                    <SunFilled style={{ color: 'black', fontSize: '16px', marginTop: '2.5px', marginLeft: '-8px' }} />
                  }
                />
              </div>
              <Dropdown
                menu={{ items }}
                trigger={['click']}
                rootClassName="headerDropdown"
                className={styles.headerDropdown}
                overlayStyle={{
                  background: theme.colors.quoteBgc,
                }}
                destroyPopupOnHide
                open={dropdownState}
                onOpenChange={(nextOpen: boolean, info) => {
                  if (info.source === 'trigger' || nextOpen) {
                    setDropdownState(nextOpen);
                  }
                }}
              >
                <div>
                  <div className={styles.userIcon}>
                    <img src={profilePictureSrc} alt="" />
                  </div>
                  <div className={styles.userInfo}>
                    <span className={styles.avatarText}>{fullname}</span>
                    <div
                      className={styles.userRoleText}
                      style={{
                        color: theme.colors.secondaryText,
                      }}
                    >
                      {userRoles.map((role: { name: string }, index: number) =>
                        index < userRoles.length - 1 ? role.name.concat(',') : role.name.concat(''),
                      )}
                    </div>
                  </div>
                  <div className={styles.menuArrowDown}>
                    <AiOutlineMenu />
                  </div>
                </div>
              </Dropdown>
            </div>
          </div>
        </div>
      </Header>
    </HeaderTheme>
  );
};

export default HeaderComponent;
